import React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';

import { Section } from './elements';
import { colors, widths } from '../utils/constants';

const Entry = props => (
  <div className={css(styles.entry)}>
    <div className={css(styles.job)}>
      <p className={css(styles.company)}>
        {props.link ? (
          <a
            href={props.link}
            className={css(styles.companyLink)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {props.company}
          </a>
        ) : (
          props.company
        )}
      </p>
      <p className={css(styles.location)}>{props.location}</p>
    </div>
    {props.children}
  </div>
);

const Position = props => {
  return (
    <div>
      <div className={css(styles.position)}>
        <p className={css(styles.title)}>{props.title}</p>
        <p className={css(styles.dates)}>{props.dates}</p>
      </div>
      <ul className={css(styles.duties)}>
        {props.duties ? props.duties.map((d, i) => <li key={i}>{d}</li>) : null}
      </ul>
    </div>
  );
};

const Resume = () => {
  return (
    <Section title="R&eacute;sum&eacute;" id="resume">
      <div>
        <h4 className={css(styles.h4)}>Experience</h4>

        <Entry company="Adobe" location="New York, NY">
          <Position
            title="Senior Computer Scientist"
            dates="Dec 2021-Present"
            duties={[
              `Developed and delivered critical front-end infrastructure for generative AI features in Adobe 
Acrobat, enabling successful cross-team collaboration and timely product release.`,
              `Led front-end development for PDF visualization platform, managing complex stakeholder
requirements and delivering a successful launch within established timelines.`,
              `Served as original member of exploratory AI initiative, driving core feature development and
successfully onboarding new team members while scaling the project.`,
              `Elevated codebase quality and team efficiency through implementation of modern development
tools and practices, including TypeScript, automated testing, and standardized code formatting.`,
              `Mentored fellow developers in React, JavaScript, and software development best practices,
contributing to team growth and improved code quality.`,
              `Demonstrated technical leadership by balancing rapid feature delivery with code quality,
establishing robust peer review processes, and serving as a trusted technical advisor across multiple
teams.`,
            ]}
          />
        </Entry>
        <Entry company="Fresh Water Systems" location="">
          <Position
            title="Independent Contractor"
            dates="April 2020-June 2022"
            duties={[
              'Converted Shopify theme to React web app, allowing new features to be implemented with greater speed, flexibility, and stability',
              'Restructured release processes to enable continuous delivery',
              'Regularly iterated on web product, contributing to 41% increase in sales year over year',
              'Coached junior developer with back-end background to become highly capable in React',
            ]}
          />
        </Entry>
        <Entry company="FemHealth" location="">
          <Position
            title="Independent Contractor"
            dates="Feb 2020-June 2023"
            duties={[
              'Architected site from scratch using Gatsby and Prismic to mirror legacy Wix site',
              'Performed ongoing maintenance and timely feature upgrades to site that women around the world rely on for critical health information',
            ]}
          />
        </Entry>
        <Entry
          company="Simple Health"
          location="New York, NY"
          link="https://www.simplehealth.com"
        >
          <Position
            title="VP of Client Engineering"
            dates="Aug 2019-Jan 2020"
            duties={[
              'Co-owned evolution of engineering organization’s processes, standards, and values',
              'Represented engineering interests and contributed to business strategy as member of company leadership team',
            ]}
          />
          <Position
            title="Director of Client Engineering"
            dates="Aug 2018-Aug 2019"
            duties={[
              'Managed web and mobile teams to reliably deliver all projects on time without sacrificing quality, with a focus on success and growth of both the individual and the company',
              'Led implementation and release of birth control offering and new company identity',
              'Fostered team morale and facilitated smooth transition during company reorganization that resulted in intense pressure and high emotions',
              'Recruited, interviewed, and hired developers as team grew, with focus on diversity and inclusion',
            ]}
          />
          <Position
            title="Engineering Manager"
            dates="Oct 2016-Aug 2018"
            duties={[
              'Led creation and launch of business’s first web product, simplecontacts.com',
              'Collaborated to implement and iterate upon first-of-its-kind online eye exam',
              'Mentored junior developers to become high-performing leaders',
            ]}
          />
        </Entry>

        <Entry
          company="Quartet Health"
          location="New York, NY"
          link="https://www.quartethealth.com"
        >
          <Position
            title="Software Engineer"
            dates="Jan 2016-Oct 2016"
            duties={[
              'Led release of new roles and permissions system to better serve multi-user practices',
              'Rebuilt entire public-facing site with admin interface',
              'Executed new health assessment functionality to improve patient matching with providers',
            ]}
          />
        </Entry>

        <Entry company="Keep" location="New York, NY">
          <Position
            title="Front End Lead"
            dates="Sept 2014-Dec 2015"
            duties={[
              'Managed front-end team projects and practices for optimal productivity and consistent delivery',
              'Oversaw conversion of universal shopping cart from native iOS to mobile webview',
              'Implemented platform to integrate unobtrusive, attractive advertising into web product',
              'Recruited and interviewed front-end candidates',
            ]}
          />
          <Position
            title="Front End Engineer"
            dates="Sept 2013-Sept 2014"
            duties={[
              'Developed interactive e-commerce and social features across the stack',
              'Created and maintained visual component library emphasizing beauty and simplicity',
              'Built administrative interface for order processing',
              'Partnered with design and marketing teams to strategically engage target audience',
            ]}
          />
        </Entry>

        <Entry
          company="Sailthru"
          location="New York, NY"
          link="https://www.sailthru.com"
        >
          <Position
            title="Front End Developer"
            dates="May 2011-Aug 2013"
            duties={[
              'Independently redesigned and built new product interface for improved user experience',
              'Collaborated with marketing team to produce professional, attractive communications',
              'Assisted customers in building clean, cross-client compatible templates',
            ]}
          />
        </Entry>

        <Entry
          company="Code and Theory"
          location="New York, NY"
          link="https://www.codeandtheory.com"
        >
          <Position
            title="Junior Developer"
            dates="Sept 2010-May 2011"
            duties={[
              'Developed full-stack solutions for major brands including Vogue and Dr. Pepper',
              'Expanded search functionality for internal content management system',
              'Optimized sites for faster performance',
            ]}
          />
        </Entry>

        <h4 className={css(styles.h4)}>Education</h4>
        <Entry company="Skidmore College">
          <Position title="Bachelor of Arts in French and Spanish (magna cum laude)" />
        </Entry>
        <Entry company="Various Institutions">
          <Position title="Additional coursework in advanced JavaScript, software design and analysis, discrete structures, linear algebra, calculus, and graphic design" />
        </Entry>

        <h4 className={css(styles.h4)}>Skills</h4>
        <Entry company="Languages">
          <Position title="JavaScript, TypeScript, CSS/Sass/LESS, HTML, Python, GraphQL, SQL" />
        </Entry>
        <Entry company="Frameworks">
          <Position title="React, Redux, MobX, React Native, Node, Express, Gatsby, Shopify, Django" />
        </Entry>
        <Entry company="Tools">
          <Position title="Git, Webpack, Jest, PhotoShop, Illustrator, InDesign, Contentful, Prismic" />
        </Entry>
        <Entry company="Human Languages">
          <Position title="Fluent French, Proficient Spanish, Basic Italian and German" />
        </Entry>
      </div>
    </Section>
  );
};

export default Resume;

const styles = StyleSheet.create({
  h4: {
    color: colors.red,
    fontWeight: 300,
    textTransform: 'uppercase',
    fontSize: 16,
    margin: '20px 0 10px 0',
  },
  entry: {
    padding: '10px 0',
    position: 'relative',
    lineHeight: 1.5,
    boxSizing: 'border-box',
    width: '100%',
  },
  job: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  company: {
    color: colors.green,
    fontWeight: 400,
    fontSize: 18,
  },
  companyLink: {
    color: 'inherit',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  position: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 5,
    [`@media ${widths.mobile}`]: {
      flexDirection: 'column',
    },
  },
  title: {
    fontWeight: 400,
  },
  column: {
    width: '40%',
    float: 'left',
    ':first-child': {
      marginRight: '20%',
    },
  },
  location: {
    textTransform: 'uppercase',
    color: colors.gray,
    fontWeight: 400,
    lineHeight: '24px',
  },
  duties: {
    listStylePosition: 'inside',
    fontWeight: 300,
  },
});
